
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Plus, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CommissionOffer } from "@/types/commission.types";
import { useUser } from "@/hooks/use-user";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useIsMobile } from "@/hooks/use-mobile";

// Schema definition
const requestSchema = z.object({
  description: z.string().min(20, "Descrição deve ter pelo menos 20 caracteres"),
  paymentMethod: z.enum(["wallet", "pix", "card"]),
});

type RequestFormValues = z.infer<typeof requestSchema>;

interface CommissionRequestFormProps {
  offer: CommissionOffer;
  onSubmit: (data: any) => void;
  isLoading: boolean;
}

export function CommissionRequestForm({ offer, onSubmit, isLoading }: CommissionRequestFormProps) {
  const { user } = useUser();
  const [references, setReferences] = useState<string[]>([]);
  const [newReference, setNewReference] = useState('');
  const isMobile = useIsMobile();

  // React Hook Form configuration
  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      description: '',
      paymentMethod: 'wallet',
    }
  });

  // Reference URLs handlers
  const addReference = () => {
    if (newReference.trim() !== '' && isValidUrl(newReference.trim())) {
      setReferences([...references, newReference.trim()]);
      setNewReference('');
    } else if (newReference.trim() !== '') {
      toast.error("URL de referência inválida");
    }
  };

  const removeReference = (refToRemove: string) => {
    setReferences(references.filter(ref => ref !== refToRemove));
  };

  // URL validation helper
  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = (values: RequestFormValues) => {
    if (!user) {
      toast.error("Você precisa estar logado para solicitar uma comissão");
      return;
    }

    const requestData = {
      commission_offer_id: offer.id,
      client_id: user.id,
      artist_id: offer.artist_id,
      reference_urls: references,
      description: values.description,
      payment_method: values.paymentMethod,
      final_price_eth: offer.price_eth,
      final_price_usd: offer.price_usd,
    };

    onSubmit(requestData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="paymentMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Método de Pagamento</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-2`}
                  >
                    <div className="flex items-center space-x-2 border rounded-md p-2">
                      <RadioGroupItem value="wallet" id="wallet" />
                      <label htmlFor="wallet" className="text-sm cursor-pointer flex items-center">
                        <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 8V6C2 4.89543 2.89543 4 4 4H20C21.1046 4 22 4.89543 22 6V8M2 8V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V8M2 8H22M18 16H18.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Carteira
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-md p-2">
                      <RadioGroupItem value="pix" id="pix" />
                      <label htmlFor="pix" className="text-sm cursor-pointer flex items-center">
                        <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.5 16L14.5 8M8 12H16M7.2 20H16.8C17.9201 20 18.4802 20 18.908 19.782C19.2843 19.5903 19.5903 19.2843 19.782 18.908C20 18.4802 20 17.9201 20 16.8V7.2C20 6.0799 20 5.51984 19.782 5.09202C19.5903 4.7157 19.2843 4.40974 18.908 4.21799C18.4802 4 17.9201 4 16.8 4H7.2C6.0799 4 5.51984 4 5.09202 4.21799C4.7157 4.40973 4.40973 4.71569 4.21799 5.09202C4 5.51984 4 6.07989 4 7.2V16.8C4 17.9201 4 18.4802 4.21799 18.908C4.40973 19.2843 4.71569 19.5903 5.09202 19.782C5.51984 20 6.07989 20 7.2 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        PIX
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-md p-2">
                      <RadioGroupItem value="card" id="card" />
                      <label htmlFor="card" className="text-sm cursor-pointer flex items-center">
                        <CreditCard className="h-4 w-4 mr-1" />
                        Cartão
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descreva em detalhes o que você deseja</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Explique com o máximo de detalhes possível o que você deseja na comissão..." 
                    className="min-h-[150px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <FormLabel>Imagens de Referência</FormLabel>
            <div className="flex items-center mt-2">
              <Input
                value={newReference}
                onChange={(e) => setNewReference(e.target.value)}
                placeholder="URL da imagem de referência"
                className="mr-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addReference();
                  }
                }}
              />
              <Button type="button" onClick={addReference} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              {references.map((url, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="relative">
                    <img 
                      src={url} 
                      alt={`Referência ${index + 1}`} 
                      className="object-cover w-full h-32"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://placehold.co/300x200?text=Imagem+Inválida';
                      }}
                    />
                    <Button 
                      type="button" 
                      size="icon" 
                      variant="destructive"
                      className="absolute top-1 right-1 h-6 w-6"
                      onClick={() => removeReference(url)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs truncate">{url}</p>
                  </CardContent>
                </Card>
              ))}
              {references.length === 0 && (
                <span className="text-sm text-muted-foreground">Nenhuma imagem de referência adicionada</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-muted/50 p-4 rounded-md">
            <h3 className="font-medium mb-2">Resumo do Pedido</h3>
            <div className="flex justify-between text-sm">
              <span>Preço:</span>
              <span className="font-medium">${offer.price_usd} (Ξ{offer.price_eth})</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span>Prazo de entrega:</span>
              <span className="font-medium">{offer.delivery_time_days} dias</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span>Revisões incluídas:</span>
              <span className="font-medium">{offer.max_revision}</span>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isLoading} className="bg-green-500 hover:bg-green-600 text-white">
              {isLoading ? 'Enviando...' : 'Solicitar Comissão'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
